import api from './api'

export type EnrollmentMode = 'USER_BOUND' | 'PROVISION'

export interface EnrollmentPayload {
    mode: EnrollmentMode
    boundUserId?: number
    agencyId?: number
    maxRedemptions?: number
    ttlMinutes?: number
    eventEndsAt?: string
}

export interface EnrollmentResponse {
    enrollmentToken: string
    qrPayload: string
    expiresAt: string
}

export interface EnrollmentTokenSummary {
    id: number
    mode: string
    grantedRole: string
    maxRedemptions: number
    redemptionCount: number
    expiresAt: string
    active: boolean
    createdAt: string
}

export interface AgentLocation {
    userId: number
    callsign: string
    latitude: number
    longitude: number
    lastUpdate: string
    team?: string
    batteryLevel?: number
}

export interface FieldMarker {
    uid: string
    callsign?: string
    latitude: number
    longitude: number
    type?: string
    remarks?: string
    createdAt: string
}

export interface FieldMarkerPayload {
    uid?: string
    callsign?: string
    lat: number
    lon: number
    type?: string
    remarks?: string
}

export interface FieldChatMessage {
    id: string
    sender: string
    message: string
    createdAt: string
    system?: boolean
}

export interface FieldStreamHandlers {
    onPosition?: (data: AgentLocation) => void
    onMarker?: (data: FieldMarker) => void
    onChat?: (data: FieldChatMessage) => void
}

// Deriva la misma baseURL que api.ts para el EventSource (SSE no usa axios).
const API_URL = import.meta.env.VITE_API_URL
    ? import.meta.env.VITE_API_URL.replace(/\/api$/, '')
    : (import.meta.env.PROD ? '' : 'http://localhost:8080')

export const fieldService = {
    async generateEnrollment(payload: EnrollmentPayload): Promise<EnrollmentResponse> {
        const { data } = await api.post<EnrollmentResponse>('/api/v1/field/enrollment', payload)
        return data
    },

    async listEnrollments(): Promise<EnrollmentTokenSummary[]> {
        const { data } = await api.get<EnrollmentTokenSummary[]>('/api/v1/field/enrollment')
        return data
    },

    async revokeEnrollment(id: number): Promise<void> {
        await api.post(`/api/v1/field/enrollment/${id}/revoke`)
    },

    async getAgents(): Promise<AgentLocation[]> {
        const { data } = await api.get<AgentLocation[]>('/api/v1/field/agents')
        return data
    },

    async getMarkers(): Promise<FieldMarker[]> {
        const { data } = await api.get<FieldMarker[]>('/api/v1/field/markers')
        return data
    },

    async postMarker(payload: FieldMarkerPayload): Promise<FieldMarker> {
        const { data } = await api.post<FieldMarker>('/api/v1/field/markers', payload)
        return data
    },

    async getChat(): Promise<FieldChatMessage[]> {
        const { data } = await api.get<FieldChatMessage[]>('/api/v1/field/chat')
        return data
    },

    async postChat(message: string): Promise<FieldChatMessage> {
        const { data } = await api.post<FieldChatMessage>('/api/v1/field/chat', { message })
        return data
    },

    /**
     * Abre un EventSource al stream SSE del tenant y registra listeners por evento
     * nombrado. Devuelve una función de cierre para detener la suscripción.
     */
    streamFieldEvents(handlers: FieldStreamHandlers): () => void {
        const es = new EventSource(`${API_URL}/api/v1/field/stream`, { withCredentials: true })

        if (handlers.onPosition) {
            es.addEventListener('position', (e) => {
                try {
                    handlers.onPosition!(JSON.parse((e as MessageEvent).data) as AgentLocation)
                } catch (err) {
                    console.error('SSE position parse error', err)
                }
            })
        }
        if (handlers.onMarker) {
            es.addEventListener('marker', (e) => {
                try {
                    handlers.onMarker!(JSON.parse((e as MessageEvent).data) as FieldMarker)
                } catch (err) {
                    console.error('SSE marker parse error', err)
                }
            })
        }
        if (handlers.onChat) {
            es.addEventListener('chat', (e) => {
                try {
                    handlers.onChat!(JSON.parse((e as MessageEvent).data) as FieldChatMessage)
                } catch (err) {
                    console.error('SSE chat parse error', err)
                }
            })
        }

        es.onerror = (err) => {
            console.error('SSE stream error', err)
        }

        return () => es.close()
    },
}

export default fieldService
